import { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Tooltip } from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import { format, isValid } from "date-fns";
import GetAppIcon from "@material-ui/icons/GetApp";
import PrintIcon from "@material-ui/icons/Print";
import { InvoiceTable } from "../invoiceSummary/InvoiceTable";
import useInvoiceList from "../../controllers/invoice/useInvoiceList";
import { StringMap } from "../../models/ui/Path";
import CaProfile from "../../models/domain/CaProfile";
import { downloadInvoicesCsv } from "../../utils/invoiceCsvExport";

function toApiDateString(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

function formatDisplayDate(date: Date): string {
  return format(date, "dd-MM-yyyy");
}

interface Props {
  profile: CaProfile;
  selectedClinicId: string;
  onClinicChange: (clinicId: string) => void;
}

export default function CaForm25Register({
  profile,
  selectedClinicId,
  onClinicChange,
}: Props) {
  const clinics = profile.clinics;
  const [fromDate, setFromDate] = useState(() => new Date());
  const [toDate, setToDate] = useState(() => new Date());
  const [status, setStatus] = useState("All");

  const fromDateApi = toApiDateString(fromDate);
  const toDateApi = toApiDateString(toDate);

  const invoiceQuery = useMemo<StringMap>(() => {
    const query: StringMap = {};
    query.date = fromDateApi;
    if (toDateApi !== fromDateApi) {
      query.toDate = toDateApi;
    }
    return query;
  }, [fromDateApi, toDateApi]);

  const handleFromDateChange = (date: Date | null) => {
    if (!date || !isValid(date)) return;
    setFromDate(date);
    if (date > toDate) setToDate(date);
  };

  const handleToDateChange = (date: Date | null) => {
    if (!date || !isValid(date)) return;
    setToDate(date);
    if (date < fromDate) setFromDate(date);
  };

  const { resource: invoiceList, isLoading, error } = useInvoiceList(
    invoiceQuery,
    { clinicId: selectedClinicId || null },
  );

  const filteredInvoices = useMemo(() => {
    return invoiceList?.invoices?.filter((inv) => {
      if (status === "All") return true;
      return (
        inv.payment_status.toLocaleLowerCase() === status.toLocaleLowerCase()
      );
    });
  }, [invoiceList, status]);

  const totals = useMemo(() => {
    return (filteredInvoices ?? []).reduce(
      (acc, inv) => {
        acc.billed += inv.total_amount;
        acc.paid += inv.paid_amount;
        acc.balance += inv.pending_amount;
        return acc;
      },
      { billed: 0, paid: 0, balance: 0 },
    );
  }, [filteredInvoices]);

  const selectedClinicName =
    clinics.find((c) => c.clinicId === selectedClinicId)?.clinicName ?? "";

  const handleDownload = () => {
    const safeClinic = selectedClinicName.replace(/\s+/g, "-") || "clinic";
    downloadInvoicesCsv(
      filteredInvoices ?? [],
      `form-25-${safeClinic}-${fromDateApi}-to-${toDateApi}.csv`,
    );
  };

  useEffect(() => {
    const onBeforePrint = () => {
      document.body.classList.add("ca-form25-printing");
    };
    const onAfterPrint = () => {
      document.body.classList.remove("ca-form25-printing");
    };
    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("afterprint", onAfterPrint);
    return () => {
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("afterprint", onAfterPrint);
      document.body.classList.remove("ca-form25-printing");
    };
  }, []);

  const handlePrint = () => {
    document.body.classList.add("ca-form25-printing");
    window.print();
  };

  if (clinics.length === 0) {
    return (
      <p className="text-muted">No clinics are linked to your CA account.</p>
    );
  }

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <div className="ca-form25-filters ca-form25-no-print">
        <div className="ca-form25-filter-field ca-form25-filter-field--clinic">
          <label className="form-label small text-muted mb-1">Clinic</label>
          <select
            className="form-select"
            value={selectedClinicId}
            onChange={(e) => onClinicChange(e.target.value)}
          >
            {clinics.map((c) => (
              <option key={c.clinicId} value={c.clinicId}>
                {c.clinicName}
              </option>
            ))}
          </select>
        </div>

        <div className="ca-form25-filter-field">
          <label className="form-label small text-muted mb-1">From Date</label>
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="dd-MM-yyyy"
            inputVariant="outlined"
            value={fromDate}
            maxDate={toDate}
            onChange={handleFromDateChange}
            autoOk
            className="ca-form25-date-picker"
            placeholder="dd-MM-yyyy"
            InputProps={{ style: { height: 38 } }}
            inputProps={{ "aria-label": "From date" }}
          />
        </div>

        <div className="ca-form25-filter-field">
          <label className="form-label small text-muted mb-1">To Date</label>
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="dd-MM-yyyy"
            inputVariant="outlined"
            value={toDate}
            minDate={fromDate}
            onChange={handleToDateChange}
            autoOk
            className="ca-form25-date-picker"
            placeholder="dd-MM-yyyy"
            InputProps={{ style: { height: 38 } }}
            inputProps={{ "aria-label": "To date" }}
          />
        </div>

        <div className="ca-form25-filter-field ca-form25-filter-field--status">
          <label className="form-label small text-muted mb-1">Status</label>
          <select
            className="form-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option>All</option>
            <option>Full</option>
            <option>Partial</option>
          </select>
        </div>

        <div className="ca-form25-filter-actions">
          <Tooltip title="Download">
            <span>
              <button
                type="button"
                className="ca-form25-icon-btn"
                onClick={handleDownload}
                disabled={!filteredInvoices?.length}
                aria-label="Download"
              >
                <GetAppIcon fontSize="small" />
              </button>
            </span>
          </Tooltip>
          <Tooltip title="Print">
            <span>
              <button
                type="button"
                className="ca-form25-icon-btn"
                onClick={handlePrint}
                disabled={!filteredInvoices?.length}
                aria-label="Print"
              >
                <PrintIcon fontSize="small" />
              </button>
            </span>
          </Tooltip>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2 small" role="alert">
          Could not load invoices. Please try again.
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : !filteredInvoices?.length ? (
        <p className="text-muted text-center py-5">
          No invoices found for the selected clinic and date range.
        </p>
      ) : (
        <div className="ca-form25-print-area">
          <div className="ca-form25-print-header">
            <h2 className="h5 fw-semibold mb-1">Form 25 Register</h2>
            <p className="small text-muted mb-0">{selectedClinicName}</p>
            <p className="small text-muted mb-0">
              {formatDisplayDate(fromDate)} to {formatDisplayDate(toDate)}
              {status !== "All" ? ` · Status: ${status}` : ""}
            </p>
          </div>
          <div className="ca-form25-table-scroll">
            <InvoiceTable
              invoices={filteredInvoices}
              totals={totals}
              readOnly
            />
          </div>
        </div>
      )}
    </MuiPickersUtilsProvider>
  );
}
